import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    MenuItem,
    Alert,
    OutlinedInput,
    InputAdornment,
    Paper,
    List,
    ListItem,
    ListItemButton
} from '@mui/material';
import { IconSearch } from '@tabler/icons';
import debounce from 'lodash.debounce';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../../../services/api';
import CustomFieldInput from '../shared/CustomFieldInput.js';
import { isFieldVisible } from '../shared/fieldVisibility';

const EditUser = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        username: '',
        role: '',
        organization: null,
        organization_name: '',
        phone: '',
        avatar: ''
    });

    const [dadosUser, setDadosUser] = useState({});
    const [status, setStatus] = useState({ type: '', message: '' });

    // 🔎 busca organization
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);

    // 🧩 campos customizados
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    // 🔥 pegar app padrão
    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    // só busca user quando tiver appId
    useEffect(() => {
        if (!appId) return;
        fetchUser();
        loadFields();
    }, [appId, id]);

    const loadFields = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/client_fields/`);
            setFieldsConfig(response.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchUser = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/users/${id}/`);
            const { custom_fields, ...rest } = response.data;
            setFormData(rest);
            setFields(custom_fields || {});
            if (rest.organization_name) {
                setSearchTerm(rest.organization_name);
            }
            setLoading(false);
            setDadosUser(rest);
        } catch (error) {
            console.error('Erro ao buscar usuario:', error);
            setLoading(false);
            setStatus({ type: 'error', message: 'Erro ao buscar usuário' });
        }
    };

    const backPage = () => {
        history.push(`/dashboard/config/users/view`);
    };

    // 🔥 update form
    const handleChange = (e) => {
        const { name, value } = e.target;

        const newData = {
            ...formData,
            [name]: value
        };

        if (name === 'first_name' || name === 'last_name') {
            const fn = name === 'first_name' ? value : formData.first_name;
            const ln = name === 'last_name' ? value : formData.last_name;

            if (fn && ln) {
                newData.username = `${fn.toLowerCase()}.${ln.toLowerCase()}`;
            }
        }

        setFormData(newData);
    };

    // 🔥 busca organization com appId
    const handleSearch = useCallback(
        debounce(async (value, currentAppId) => {
            if (!value || !currentAppId) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }

            try {
                const response = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);

                setSearchResults(response.data.organizations || response.data);
                setShowResults(true);
            } catch (error) {
                console.error('Erro na busca:', error);
                setStatus({ type: 'error', message: 'Erro ao buscar organizações' });
            }
        }, 400),
        []
    );

    const handleChangeSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!appId) return;

        handleSearch(value, appId);
    };

    const handleSelectOrganization = (org) => {
        setFormData({
            ...formData,
            organization: org.id,
            organization_name: org.name
        });

        setSearchTerm(org.name);
        setShowResults(false);
    };

    const clearOrganization = () => {
        setFormData({
            ...formData,
            organization: null,
            organization_name: ''
        });

        setSearchTerm('');
        setShowResults(false);
    };

    const handleDeleteUser = async () => {
        try {
            await api.delete(`v1/apps/${appId}/users/${id}/`);
            setStatus({ type: 'success', message: 'Usuário apagado com sucesso!' });
            backPage();
        } catch (error) {
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || 'Erro ao apagar usuário.'
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        try {
            const visibleNames = new Set(fieldsConfig.filter((f) => isFieldVisible(f, fieldsConfig, fields)).map((f) => f.name));
            const fieldsToSend = Object.fromEntries(Object.entries(fields).filter(([name]) => visibleNames.has(name)));

            await api.put(`v1/apps/${appId}/users/${id}/`, { ...formData, fields: fieldsToSend });

            setStatus({ type: 'success', message: 'Usuário editado com sucesso!' });
            backPage();
        } catch (error) {
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || 'Erro ao editar usuário.'
            });
        }
    };

    if (loading) return <Typography>Carregando...</Typography>;

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                Editar Usuário
            </Typography>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            {/* Nomes */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="first_name"
                                    label="Primeiro Nome"
                                    fullWidth
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="last_name"
                                    label="Segundo Nome"
                                    fullWidth
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>

                            {/* Username gerado automaticamente */}
                            <Grid item xs={12}>
                                <TextField name="username" label="Username" fullWidth value={formData.username} disabled />
                            </Grid>

                            {/* Email */}
                            <Grid item xs={12} sm={6}>
                                <TextField name="email" label="Email" fullWidth value={formData.email} onChange={handleChange} required />
                            </Grid>

                            {/* {dadosUser.role === 'admin' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="role"
                                        select
                                        label="Nível de Permissão"
                                        fullWidth
                                        value={formData.role}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="admin">Administrador</MenuItem>
                                        <MenuItem value="agent">Membro da Empresa</MenuItem>
                                        <MenuItem value="customer">Usuário Final</MenuItem>
                                    </TextField>
                                </Grid>
                            )}
                            {(dadosUser.role === 'agent' || dadosUser.role === 'customer') && (
                                <Grid item xs={12} sm={6}>
                                    <TextField name="role" label="Nível de Permissão" fullWidth value={formData.role}>
                                        <MenuItem value="admin">Administrador</MenuItem>
                                        <MenuItem value="agent">Membro da Empresa</MenuItem>
                                        <MenuItem value="customer">Usuário Final</MenuItem>
                                    </TextField>
                                </Grid>
                            )} */}

                            {/* Campo de busca da Organização */}
                            <Grid item xs={12}>
                                <OutlinedInput
                                    fullWidth
                                    placeholder="Buscar organização por nome, CNPJ ou CPF..."
                                    value={searchTerm}
                                    onChange={handleChangeSearch}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <IconSearch size="1.2rem" color="#666" />
                                        </InputAdornment>
                                    }
                                />
                            </Grid>

                            {/* Lista de resultados */}
                            {showResults && (
                                <Grid item xs={12}>
                                    <Paper elevation={3}>
                                        <List>
                                            {searchResults.length === 0 && <ListItem>Nenhuma organização encontrada</ListItem>}

                                            {searchResults.map((org) => (
                                                <ListItem disablePadding key={org.id}>
                                                    <ListItemButton onClick={() => handleSelectOrganization(org)}>
                                                        {org.name}
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}

                                            {/* opção de deixar sem organização */}
                                            <ListItem disablePadding>
                                                <ListItemButton onClick={clearOrganization}>Sem organização</ListItemButton>
                                            </ListItem>
                                        </List>
                                    </Paper>
                                </Grid>
                            )}

                            {/* Telefone */}
                            <Grid item xs={12} sm={6}>
                                <TextField name="phone" label="Telefone" fullWidth value={formData.phone} onChange={handleChange} />
                            </Grid>

                            {/* Avatar */}
                            {/* <Grid item xs={12} sm={6}>
                                <TextField name="avatar" label="Avatar (URL)" fullWidth value={formData.avatar} onChange={handleChange} />
                            </Grid> */}

                            {fieldsConfig
                                .filter((f) => isFieldVisible(f, fieldsConfig, fields))
                                .map((f) => (
                                    <CustomFieldInput
                                        key={f.id}
                                        field={f}
                                        value={fields[f.name]}
                                        onChange={(value) => setFields({ ...fields, [f.name]: value })}
                                    />
                                ))}

                            {/* Botões */}
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    {/* LIMPAR */}
                                    <Button
                                        variant="outlined"
                                        onClick={backPage}
                                        sx={{
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1,
                                            color: '#ccc',
                                            borderColor: '#555',
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: '#999',
                                                background: 'rgba(255,255,255,0.05)'
                                            }
                                        }}
                                    >
                                        Voltar
                                    </Button>

                                    <Button
                                        onClick={handleDeleteUser}
                                        sx={{
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                                            color: '#fff',
                                            boxShadow: '0 8px 20px rgba(239,68,68,0.4)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 30px rgba(239,68,68,0.6)'
                                            }
                                        }}
                                    >
                                        Apagar
                                    </Button>

                                    <Button
                                        type="submit"
                                        sx={{
                                            borderRadius: '12px',
                                            px: 4,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #22c55e, #4ade80)',
                                            color: '#000',
                                            boxShadow: '0 8px 20px rgba(34,197,94,0.4)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 30px rgba(34,197,94,0.6)'
                                            }
                                        }}
                                    >
                                        💾 Salvar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default EditUser;
